import { GenericRequest, HeadersJson } from './data/AuthTypes';
import { stringConstants } from '../AuthConstants';
import AuthUtils from '../AuthUtils';
import { AuthenticateResponse } from './data/AuthenticateResponse';
import { HTTPFetchResponse } from './data/AuthTypes';
import DSAuth from './DSAuth';
import { Logger } from '../Logger';
import { SentryLogger } from '../../../src/sentryLogging/SentryLogger';
import { logConstants } from '../../../src/utils/Constants';
import {
	PrimaryTokenFetchFailure,
	MissingAuthRequirementsError,
	authErrorString,
} from '../AuthErrors';
import { XMLBaseParser } from '../../utils/XMLBaseParser';
import { HttpStatusCodes } from '../../utils/http/HttpConstants';

export default class ExplicitFormsHelper {
	constructor(readonly xmlParser: XMLBaseParser<AuthenticateResponse>) {
		if (xmlParser === undefined || xmlParser == null) {
			throw TypeError('xmlParser is undefined or null');
		}
	}

	async getExplicitFormsRequestResponse(
		authProtocolsRequest: GenericRequest,
		explicitFormsLocation: string,
		referenceUri: string,
		id: string,
		isOnPrem: boolean,
		additionalHeaders?: HeadersJson
	): Promise<AuthenticateResponse> {
		const explicitFormsRequest = this.prepareExplicitFormsRequest(
			authProtocolsRequest,
			explicitFormsLocation,
			isOnPrem,
			additionalHeaders
		);
		const explicitFormsResponse = await this.doExplicitForms(
			explicitFormsRequest,
			referenceUri,
			id
		);
		return this.processExplicitFormsResponse(explicitFormsResponse, id);
	}

	processExplicitFormsResponse(
		explicitFormsResponse: HTTPFetchResponse,
		id: string
	): AuthenticateResponse {
		const { statusCode, body } = explicitFormsResponse;
		if (statusCode != HttpStatusCodes.HTTP_STATUS_OK) {
			const errorMsg =
				id +
				':' +
				authErrorString.DSAUTH_MISSING_REQUIREMENTS +
				' StatusCode : ' +
				statusCode;
			Logger.getInstance().write(logConstants.ERROR, errorMsg);
			const innerError = new MissingAuthRequirementsError(errorMsg);
			throw new PrimaryTokenFetchFailure(innerError);
		}
		try {
			const authResp: AuthenticateResponse = this.xmlParser.parseXML(
				body
			) as AuthenticateResponse;
			Logger.getInstance().write(
				logConstants.INFO,
				id + ':ExplicitForms : ' + JSON.stringify(authResp)
			);
			return authResp;
		} catch (e) {
			Logger.getInstance().write(
				logConstants.INFO,
				id + ':DSAUTH_PROCESS_EXPLICIT_FORMS_REPSONSE_FAIL:' + e.message
			);
			SentryLogger.getInstance().logError(e, {
				customMessage: 'processing explicit forms response failed',
				additionalContext: {
					innerError: e.innerError,
				},
			});
			throw new PrimaryTokenFetchFailure(e);
		}
	}

	private prepareExplicitFormsRequest(
		authProtocolsRequest: GenericRequest,
		explicitFormsLocation: string,
		isOnPrem: boolean,
		additionalHeaders?: HeadersJson
	): GenericRequest {
		const { headers, body } = authProtocolsRequest;
		return {
			method: stringConstants.POST,
			headers: {
				...AuthUtils.getInstance().addLabelAndCredTypesToRequestHeader(headers, isOnPrem),
				...additionalHeaders,
			},
			body: body,
			url: explicitFormsLocation,
		};
	}

	/// <summary>
	/// make ExplicitForms request
	/// example request url : https://accounts-dsauthweb.cloud.com/ECzs9gI6kU-DF5FdvJZFVQ/ExplicitForms/Start
	/// </summary>
	private async doExplicitForms(
		explicitFormsRequest: GenericRequest,
		referenceUri: string,
		id: string
	): Promise<HTTPFetchResponse> {
		const { method, headers, body, url } = explicitFormsRequest;
		try {
			return await DSAuth.getInstance().fetch(
				method,
				url,
				headers,
				body,
				referenceUri,
				false
			);
		} catch (e) {
			Logger.getInstance().write(
				logConstants.ERROR,
				`${id}: DSAUTH_DO_EXPLICIT_FORMS_FAILURE: ${e.message}`
			);
			throw new PrimaryTokenFetchFailure(e);
		}
	}
}
